import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { CheckCircle2, Circle, ArrowRight, Award, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
    buildHerramientasSteps,
    herramientasDismissKey,
    herramientasCelebratedKey,
    HerramientasCtx,
} from './primeros-pasos-steps';
import { useTour } from '../tours/TourContext';
import { TOURS } from '../tours/tour-definitions';

interface PrimerosPasosProps {
    theme?: 'light' | 'dark';
}

// "Herramientas clave" checklist — the 6 key tools, derived per-user from live
// data (steps in primeros-pasos-steps.ts, mirrored on mobile). Completing all
// 6 earns the permanent tools badge via the award_tools_badge() RPC, which
// re-verifies server-side. Self-hides once the badge is earned + celebrated;
// dismissible earlier, but the award check still runs while dismissed so the
// badge is never missed.
export const PrimerosPasos: React.FC<PrimerosPasosProps> = ({ theme = 'dark' }) => {
    const isLight = theme !== 'dark';
    const navigate = useNavigate();
    const { startTour } = useTour();
    const { profile } = useAuth();
    const userId = profile?.id ? String(profile.id) : '';

    const dismissKey = herramientasDismissKey(userId);
    const celebratedKey = herramientasCelebratedKey(userId);
    const [dismissed, setDismissed] = useState<boolean>(() => {
        try { return localStorage.getItem(dismissKey) === '1'; } catch { return false; }
    });
    const [ctx, setCtx] = useState<HerramientasCtx | null>(null);
    // Badge state starts from the profile (covers backfilled veterans) and is
    // flipped locally when the award RPC succeeds mid-session.
    const [badgeEarned, setBadgeEarned] = useState<boolean>(!!profile?.tools_badge_earned_at);
    const [celebrated, setCelebrated] = useState<boolean>(() => {
        try { return localStorage.getItem(celebratedKey) === '1'; } catch { return false; }
    });

    useEffect(() => {
        setBadgeEarned(!!profile?.tools_badge_earned_at);
    }, [profile?.tools_badge_earned_at]);

    useEffect(() => {
        if (!supabase || !userId || badgeEarned) return;
        let cancelled = false;
        (async () => {
            const own = `agent_id.eq.${userId},created_by.eq.${userId}`;
            // "Ever did it" semantics — keep in sync with award_tools_badge()
            // (supabase/migrations/20260823160000_tools_badge.sql).
            const exists = (q: PromiseLike<{ count: number | null }>) =>
                q.then(r => (r.count ?? 0) > 0);
            const [published, sharedProperty, sharedRequirement, vitrina, contract, report] = await Promise.all([
                exists(supabase!.from('properties').select('id', { count: 'exact', head: true })
                    .or(own).or('publish_status.eq.published,first_published_at.not.is.null,published_at.not.is.null')),
                exists(supabase!.from('properties').select('id', { count: 'exact', head: true })
                    .or(own).or('share_status.eq.shared,first_shared_at.not.is.null,shared_at.not.is.null')),
                exists(supabase!.from('requirements').select('id', { count: 'exact', head: true })
                    .or(own).or('is_shared.eq.true,shared_at.not.is.null')),
                exists(supabase!.from('property_showcases').select('id', { count: 'exact', head: true })
                    .eq('created_by', userId)),
                exists(supabase!.from('owner_authorizations').select('id', { count: 'exact', head: true })
                    .eq('created_by', userId)),
                exists(supabase!.from('property_reports').select('id', { count: 'exact', head: true })
                    .eq('created_by', userId)),
            ]);
            if (cancelled) return;
            const next = { published, sharedProperty, sharedRequirement, vitrina, contract, report };
            setCtx(next);
            if (Object.values(next).every(Boolean)) {
                // Server re-verifies, so a spoofed client gains nothing.
                const { data } = await supabase!.rpc('award_tools_badge');
                if (!cancelled && (data as any)?.awarded) setBadgeEarned(true);
            }
        })();
        return () => { cancelled = true; };
    }, [userId, badgeEarned]);

    if (!userId) return null;

    const markCelebrated = () => {
        try { localStorage.setItem(celebratedKey, '1'); } catch { /* ignore */ }
        setCelebrated(true);
    };

    // Badge earned (now or backfilled): one celebration view, then gone forever.
    if (badgeEarned) {
        if (celebrated) return null;
        return (
            <div className={clsx(
                'mb-8 rounded-2xl border overflow-hidden p-6 text-center',
                isLight ? 'bg-gradient-to-br from-amber-50 to-white border-amber-200 shadow-sm' : 'bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30'
            )}>
                <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-3">
                    <Award size={30} className="text-amber-500" />
                </div>
                <h3 className={clsx('font-black text-lg mb-1', isLight ? 'text-slate-900' : 'text-white')}>
                    ¡Ganaste la insignia Agente Experto!
                </h3>
                <p className={clsx('text-sm mb-4 max-w-md mx-auto', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    Dominas las 6 herramientas clave de la plataforma. Tu insignia ahora es visible para tus colegas de la Red de Intercambio.
                </p>
                <button
                    onClick={markCelebrated}
                    className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors"
                >
                    ¡Genial!
                </button>
            </div>
        );
    }

    if (dismissed || !ctx) return null;

    const steps = buildHerramientasSteps(ctx);
    const doneCount = steps.filter(s => s.done).length;
    const nextKey = steps.find(s => !s.done)?.key;

    const dismiss = () => {
        try { localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div className={clsx(
            'mb-8 rounded-2xl border overflow-hidden',
            isLight ? 'bg-white border-indigo-100 shadow-sm' : 'bg-white/5 border-indigo-500/20'
        )}>
            <div className="flex items-center justify-between px-5 py-3.5">
                <span className={clsx('flex items-center gap-2 font-bold text-sm', isLight ? 'text-slate-900' : 'text-white')}>
                    <Award size={16} className="text-amber-500" /> Herramientas clave
                </span>
                <span className="flex items-center gap-3">
                    <span className={clsx('text-xs font-black', isLight ? 'text-indigo-600' : 'text-indigo-400')}>
                        {doneCount}/{steps.length}
                    </span>
                    <button
                        onClick={dismiss}
                        title="Ocultar esta lista"
                        aria-label="Ocultar esta lista"
                        className={clsx('p-1 rounded-lg transition-colors', isLight ? 'text-slate-300 hover:text-slate-500 hover:bg-slate-100' : 'text-slate-600 hover:text-slate-300 hover:bg-white/10')}
                    >
                        <X size={14} />
                    </button>
                </span>
            </div>
            <p className={clsx('px-5 -mt-1.5 mb-2 text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>
                Prueba las 6 herramientas que más ventas generan y gana la insignia <span className="font-bold text-amber-500">Agente Experto</span>, visible para tus colegas.
            </p>
            <div className={clsx('h-1 mx-5 mb-1 rounded-full overflow-hidden', isLight ? 'bg-slate-100' : 'bg-white/10')}>
                <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
            </div>
            <div className={clsx('divide-y', isLight ? 'divide-slate-50' : 'divide-white/5')}>
                {steps.map(s => (
                    <button
                        key={s.key}
                        onClick={() => { if (s.done) return; if (TOURS[s.key]) startTour(s.key); else navigate(s.to); }}
                        disabled={s.done}
                        className={clsx(
                            'w-full flex items-center gap-3 px-5 py-3 text-left text-sm transition-colors',
                            s.done ? 'cursor-default' : (isLight ? 'hover:bg-indigo-50/50' : 'hover:bg-white/5'),
                            s.key === nextKey && (isLight ? 'bg-amber-50/60' : 'bg-amber-500/5')
                        )}
                    >
                        {s.done
                            ? <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                            : <Circle size={18} className={clsx('shrink-0', isLight ? 'text-slate-300' : 'text-slate-600')} />}
                        <span className={clsx(
                            'flex-1',
                            s.done
                                ? 'line-through ' + (isLight ? 'text-slate-400' : 'text-slate-500')
                                : (isLight ? 'text-slate-700 font-semibold' : 'text-slate-200 font-semibold')
                        )}>
                            {s.label}
                        </span>
                        {s.key === nextKey && (
                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-500 shrink-0">Siguiente</span>
                        )}
                        {!s.done && <ArrowRight size={14} className={isLight ? 'text-indigo-400' : 'text-indigo-500'} />}
                    </button>
                ))}
            </div>
        </div>
    );
};
